import React from 'react';
import { Modal, Button, Typography, Space, Tag, Collapse } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { SourceProcess, ExtractedScene } from '../../../types';

const { Text } = Typography;

interface ScenePreviewModalProps {
  visible: boolean;
  onCancel: () => void;
  process: SourceProcess | null;
  extractedScenes: ExtractedScene[];
  onConfirm: () => void;
  loading: boolean;
  getCharacterName: (id: string) => string;
}

const ScenePreviewModal: React.FC<ScenePreviewModalProps> = ({
  visible,
  onCancel,
  process,
  extractedScenes,
  onConfirm,
  loading,
  getCharacterName,
}) => {
  return (
    <Modal
      title={`AI Extracted Scenes - ${process?.name}`}
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<CheckCircleOutlined />}
          onClick={onConfirm}
          loading={loading}
        >
          Save All Scenes
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          AI has extracted <Text strong>{extractedScenes.length}</Text> important scenes.
          Review them below and click "Save All Scenes" to proceed.
        </Text>
      </div>
      <Collapse accordion defaultActiveKey={['0']}>
        {extractedScenes.map((scene, index) => (
          <Collapse.Panel
            header={
              <Space>
                <Tag color="blue">#{index + 1}</Tag>
                <Text strong>{scene.title}</Text>
                {scene.reason && (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    Important: {scene.reason.substring(0, 40)}...
                  </Tag>
                )}
              </Space>
            }
            key={index.toString()}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Summary:</Text>
                <div style={{ marginTop: 4, padding: 8, background: '#fafafa', borderRadius: 4 }}>
                  {scene.summary}
                </div>
              </div>
              <div>
                <Text strong>Setting:</Text>
                <div style={{ marginTop: 4, padding: 8, background: '#fafafa', borderRadius: 4 }}>
                  {scene.setting}
                </div>
              </div>
              <div>
                <Text strong>Characters: </Text>
                <div style={{ marginTop: 4 }}>
                  {scene.characterIds.map(id => (
                    <Tag key={id} color="blue">{getCharacterName(id)}</Tag>
                  ))}
                </div>
              </div>
              <div>
                <Text strong>Key Moments:</Text>
                <div style={{ marginTop: 4 }}>
                  {scene.keyMoments?.map((moment, i) => (
                    <Tag key={i} color="cyan" style={{ marginBottom: 4 }}>{moment}</Tag>
                  ))}
                </div>
              </div>
              <div>
                <Text strong>End State:</Text>
                <div style={{ marginTop: 4, padding: 8, background: '#f6ffed', borderRadius: 4, borderLeft: '3px solid #52c41a' }}>
                  {scene.endState}
                </div>
              </div>
              <div>
                <Text strong>Why this scene matters:</Text>
                <div style={{ marginTop: 4, padding: 8, background: '#fffbe6', borderRadius: 4, borderLeft: '3px solid #faad14' }}>
                  {scene.reason}
                </div>
              </div>
            </Space>
          </Collapse.Panel>
        ))}
      </Collapse>
    </Modal>
  );
};

export default ScenePreviewModal;
